import { Close, Download } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { hostname } from "../../../utils";

interface WhitelabelAgreementDialogProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

const AGREEMENT_VERSION = "2.0";

const WhitelabelAgreementDialog: React.FC<WhitelabelAgreementDialogProps> = ({
  open,
  onAccept,
  onDecline,
}) => {
  const [accepted, setAccepted] = useState(false);

  const handleAccept = () => {
    if (accepted) {
      onAccept();
    }
  };

  const handleDecline = () => {
    setAccepted(false);
    onDecline();
  };

  const handleDownloadPdf = () => {
    window.open(`${hostname}/whitelabel/agreement/pdf`, "_blank");
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={handleDecline}
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h5" component="div">
          Whitelabel Partner Agreement
        </Typography>
        <IconButton onClick={handleDecline} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Please read and accept the following agreement to proceed with
          creating your whitelabel instance.
        </Alert>

        <Paper
          elevation={0}
          sx={{ p: 3, maxHeight: "400px", overflowY: "auto" }}
        >
          <Typography variant="h6" gutterBottom>
            WHITELABEL PARTNER AGREEMENT
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Version {AGREEMENT_VERSION} - Effective Date: December 2025
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              1. DEFINITIONS
            </Typography>
            <Typography variant="body2" paragraph>
              • "Platform" means the GTM AI software-as-a-service platform
              and all associated services, APIs, and documentation.
              • "Whitelabel Instance" means a customized version of the Platform
              operated under your brand identity.
              • "End Customers" means any individuals or entities to whom you
              provide access to the Whitelabel Instance.
              • "Customer Data" means all data, content, and information uploaded,
              processed, or generated by End Customers through the Whitelabel Instance.
              • "Confidential Information" means non-public information disclosed by
              either party, including business strategies, technical specifications,
              customer lists, and pricing information.
              • "AI Services" means artificial intelligence features including language
              models, content generation, and automated processing capabilities.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              2. GRANT OF LICENSE
            </Typography>
            <Typography variant="body2" paragraph>
              Subject to the terms and conditions of this Agreement, we grant
              you a non-exclusive, non-transferable, revocable license to
              operate a Whitelabel Instance under your own brand identity. This
              license permits you to:
              • Customize the platform appearance with your branding elements
              • Offer the platform services to End Customers under your brand
              • Set your own pricing structure for End Customers
              • Manage End Customer relationships directly
              • Access and use AI Services to power your Whitelabel Instance
              • Integrate the Platform with your existing business systems
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              3. BRANDING AND CUSTOMIZATION
            </Typography>
            <Typography variant="body2" paragraph>
              You are authorized to:
              • Upload and display your company logos, colors, and branding materials
              • Customize the domain name for your Whitelabel Instance
              • Modify customer-facing text, messaging, and email templates
              • Configure custom support contact information
              • Set up custom terms of service and privacy policy URLs
            </Typography>
            <Typography variant="body2" paragraph>
              All customizations must comply with our brand guidelines and may not:
              • Misrepresent the relationship between our companies
              • Infringe on third-party intellectual property rights
              • Contain offensive, discriminatory, illegal, or inappropriate content
              • Include malware, harmful code, or security vulnerabilities
              • Violate any applicable laws or regulations
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              4. CUSTOMER RELATIONSHIPS AND RESPONSIBILITIES
            </Typography>
            <Typography variant="body2" paragraph>
              You maintain full ownership of your End Customer relationships and agree to:
              • Provide all first-line customer support, billing, and service delivery
              • Maintain your own legally compliant terms of service for End Customers
              • Maintain your own legally compliant privacy policy for End Customers
              • Ensure your terms adequately inform End Customers about AI processing
              • Obtain all necessary consents for data processing and AI usage
              • Respond to End Customer data subject requests within required timeframes
              • Handle all End Customer complaints, disputes, and refund requests
              • Maintain accurate records of End Customer agreements and consents
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              5. REVENUE SHARING AND FEES
            </Typography>
            <Typography variant="body2" paragraph>
              Revenue sharing and fee terms are established in your subscription agreement
              and may include:
              • Monthly or annual platform licensing fees
              • Per-seat or per-user licensing fees
              • Revenue sharing percentages on End Customer payments
              • Usage-based fees for AI processing, storage, and bandwidth
              • Transaction fees for payment processing
              • Overage charges for exceeding plan limits
            </Typography>
            <Typography variant="body2" paragraph>
              You are solely responsible for:
              • Collecting payments from your End Customers
              • Managing billing disputes with End Customers
              • Payment of platform fees regardless of your collection success
              • All taxes applicable to your End Customer transactions
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              6. DATA PRIVACY AND PROTECTION
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Data Processing:</strong> You act as the data controller for Customer
              Data, and we act as a data processor on your behalf. You agree to:
              • Implement appropriate technical and organizational security measures
              • Comply with GDPR, CCPA, and all applicable data protection regulations
              • Obtain valid legal bases for processing End Customer personal data
              • Maintain records of processing activities as required by law
              • Conduct data protection impact assessments where required
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Security Obligations:</strong> You must:
              • Implement encryption at rest (AES-256) and in transit (TLS 1.2+) for Customer Data
              • Maintain access controls and authentication requirements
              • Conduct regular security assessments and vulnerability testing
              • Notify us within 24 hours of any data breaches or security incidents
              • Maintain incident response and disaster recovery procedures
              • Ensure employee security training and access controls
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Data Retention:</strong> Upon termination:
              • You must export or delete Customer Data within 30 days
              • We will delete Customer Data from our systems within 90 days
              • Certain data may be retained as required by law or for legitimate purposes
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              7. AI SERVICES TERMS
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>AI Processing:</strong> The Platform uses artificial intelligence
              to process Customer Data. You acknowledge and agree that:
              • AI-generated content is provided "as is" without accuracy guarantees
              • You are responsible for reviewing and verifying AI outputs before use
              • AI outputs may not be factually accurate, complete, or suitable for all purposes
              • We do not use Customer Data to train AI models without explicit consent
              • We implement zero data retention policies with AI providers where available
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>End Customer Disclosure:</strong> You must inform End Customers that:
              • AI is used to process their data and generate content
              • AI outputs should be reviewed for accuracy before reliance
              • Their data may be processed by third-party AI providers
              • They should not input sensitive personal data into AI features
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Prohibited AI Uses:</strong> You and your End Customers may not use AI Services to:
              • Generate illegal, harmful, or objectionable content
              • Create deepfakes or synthetic media for deception
              • Engage in spam, phishing, or fraudulent activities
              • Infringe on intellectual property or privacy rights
              • Make automated decisions with significant legal effects without human review
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              8. INTELLECTUAL PROPERTY
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Our IP:</strong> We retain all rights, title, and interest in:
              • The Platform technology, software, and source code
              • Our trademarks, service marks, and branding
              • Documentation, APIs, and technical specifications
              • Any improvements or derivatives of the Platform
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Your IP:</strong> You retain all rights to:
              • Your branding materials, logos, and trademarks
              • Customer Data and End Customer relationships
              • Content you create independently of the Platform
              • AI-generated outputs created using your inputs (subject to third-party AI provider terms)
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>License Grants:</strong>
              • You grant us a limited license to use your branding solely to operate your Whitelabel Instance
              • Neither party may use the other's trademarks without prior written permission
              • Any feedback you provide becomes our property and may be used without restriction
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              9. PROHIBITED ACTIVITIES
            </Typography>
            <Typography variant="body2" paragraph>
              You and your End Customers may not:
              • Resell, sublicense, or transfer whitelabel rights to third parties
              • Use the Platform for illegal, fraudulent, or harmful activities
              • Attempt to compete directly with our main platform offering
              • Misrepresent your relationship with us or make false platform claims
              • Exceed usage limits or attempt to circumvent restrictions
              • Share access credentials or allow unauthorized access
              • Reverse engineer, decompile, or attempt to extract source code
              • Interfere with or disrupt Platform integrity or performance
              • Use the Platform to build a competitive product or service
              • Engage in spam, phishing, or deceptive practices
              • Generate content that infringes intellectual property rights
              • Attempt to gain unauthorized access to our systems
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              10. SERVICE LEVEL AND SUPPORT
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Our Responsibilities:</strong>
              • Maintain Platform availability with commercially reasonable uptime
              • Provide platform maintenance and core technical support
              • Implement security patches and critical updates
              • Provide reasonable notice for scheduled maintenance
              • Make available technical documentation and APIs
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Your Responsibilities:</strong>
              • Provide all first-line End Customer support
              • Escalate only confirmed platform issues to our support
              • Maintain accurate End Customer documentation
              • Train your support staff on Platform features
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Service Limitations:</strong>
              • We do not guarantee uninterrupted or error-free service
              • Planned maintenance windows will be communicated in advance
              • Force majeure events are excluded from availability calculations
              • Service level agreements (SLAs) are defined separately if applicable
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              11. TERM AND TERMINATION
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Term:</strong> This agreement begins upon acceptance and continues
              on a month-to-month basis unless terminated or superseded by a different
              subscription term.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Termination for Convenience:</strong> Either party may terminate
              with 30 days written notice for any reason.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Termination for Cause:</strong> We may terminate immediately if you:
              • Breach any material term of this Agreement
              • Fail to pay fees within 15 days of due date
              • Engage in fraudulent, illegal, or harmful activities
              • Misuse AI Services or violate acceptable use policies
              • Experience a change of control to a competitor
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Effect of Termination:</strong>
              • All licenses granted immediately terminate
              • You must cease all use of the Platform
              • You must export or delete Customer Data within 30 days
              • Payment obligations for services rendered survive termination
              • Sections on IP, confidentiality, liability, and indemnification survive
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              12. WARRANTIES AND DISCLAIMERS
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Our Limited Warranty:</strong> We warrant that the Platform will
              perform substantially in accordance with its documentation during the term.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>DISCLAIMER:</strong> EXCEPT AS EXPRESSLY PROVIDED, THE PLATFORM IS
              PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS
              OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT:
              • The Platform will be uninterrupted, secure, or error-free
              • AI outputs will be accurate, complete, or suitable for any purpose
              • The Platform will meet your specific requirements
              • Results obtained will be accurate or reliable
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              13. LIMITATION OF LIABILITY
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>EXCLUSION OF DAMAGES:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW,
              NEITHER PARTY SHALL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING:
              • Loss of profits, revenue, data, or business opportunities
              • Damages from service interruptions or downtime
              • Damages from unauthorized access to data
              • Damages from AI-generated content or recommendations
              • Damages arising from End Customer claims or actions
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>LIABILITY CAP:</strong> Our total aggregate liability shall not exceed
              the greater of: (a) fees paid by you in the twelve (12) months preceding the
              claim, or (b) one thousand dollars ($1,000).
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>EXCEPTIONS:</strong> These limitations do not apply to: (a) breaches of
              confidentiality obligations, (b) your indemnification obligations, (c) your
              payment obligations, or (d) gross negligence or willful misconduct.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              14. INDEMNIFICATION
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Your Indemnification:</strong> You agree to defend, indemnify, and hold
              harmless us and our affiliates, officers, directors, employees, and agents
              from any claims, damages, losses, liabilities, costs, and expenses arising from:
              • Your use of the Platform or Whitelabel Instance
              • Your violation of this Agreement or applicable laws
              • Your End Customers' use of the Whitelabel Instance
              • Your End Customers' content or data
              • Your failure to obtain proper consents
              • Claims by End Customers or third parties
              • Your use or distribution of AI-generated content
              • Your branding, marketing, or representations
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Our Indemnification:</strong> We will defend you against third-party
              claims that the Platform infringes valid patents or copyrights, provided you:
              • Promptly notify us of the claim
              • Give us sole control of the defense and settlement
              • Provide reasonable assistance at our expense
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              15. CONFIDENTIALITY
            </Typography>
            <Typography variant="body2" paragraph>
              Both parties agree to:
              • Maintain confidentiality of Confidential Information
              • Use Confidential Information only for purposes of this Agreement
              • Protect Confidential Information with at least the same care used for own confidential information
              • Limit disclosure to employees and contractors with need to know
              • Not disclose Confidential Information to third parties without consent
            </Typography>
            <Typography variant="body2" paragraph>
              Confidential Information does not include information that:
              • Is or becomes publicly available without breach
              • Was known prior to disclosure
              • Is independently developed without use of Confidential Information
              • Is disclosed pursuant to legal requirement with notice
            </Typography>
            <Typography variant="body2" paragraph>
              This confidentiality obligation survives termination for three (3) years.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              16. COMPLIANCE AND INTERNATIONAL USE
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Legal Compliance:</strong> You must comply with all applicable laws,
              including:
              • Data protection laws (GDPR, CCPA, and local equivalents)
              • Anti-spam and electronic marketing laws (CAN-SPAM, GDPR)
              • Consumer protection and advertising laws
              • Export control and sanctions regulations
              • Industry-specific regulations (healthcare, finance, etc.)
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>International Transfers:</strong> You acknowledge that:
              • The Platform is operated from the United States
              • Customer Data may be transferred to and processed in the United States
              • You are responsible for compliance with cross-border transfer requirements
              • Standard contractual clauses are available upon request for EEA transfers
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Export Compliance:</strong> You represent that you are not:
              • Located in a U.S. embargoed country
              • Listed on any U.S. government restricted party list
              • Using the Platform for prohibited end uses
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              17. DISPUTE RESOLUTION
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Governing Law:</strong> This Agreement is governed by the laws of
              Delaware, United States, without regard to conflict of law principles.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Informal Resolution:</strong> Before formal proceedings, parties agree
              to attempt good faith resolution for 30 days by contacting the other party's
              designated representative.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Arbitration:</strong> Disputes not resolved informally shall be
              resolved through binding arbitration under the American Arbitration Association
              rules in Delaware. Each party bears its own costs, and the arbitrator's
              decision is final and binding.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Class Action Waiver:</strong> All disputes must be brought individually.
              You waive any right to participate in class actions or class-wide arbitration.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              18. MODIFICATIONS
            </Typography>
            <Typography variant="body2" paragraph>
              • We may update this Agreement with 30 days notice via email or platform notification
              • Material changes may require explicit re-acceptance
              • Continued use after the notice period constitutes acceptance
              • If you do not agree to changes, you may terminate before they take effect
              • We will maintain previous versions for reference
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              19. INSURANCE
            </Typography>
            <Typography variant="body2" paragraph>
              You agree to maintain appropriate insurance coverage, including:
              • General commercial liability insurance
              • Professional liability / errors and omissions insurance
              • Cyber liability insurance covering data breaches
              • Coverage amounts appropriate for your business operations
              • Evidence of insurance available upon request
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              20. GENERAL PROVISIONS
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Entire Agreement:</strong> This Agreement, together with any referenced
              documents and your subscription agreement, constitutes the entire agreement
              and supersedes all prior agreements.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Severability:</strong> If any provision is found unenforceable, the
              remaining provisions continue in full force.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Waiver:</strong> Failure to enforce any right does not constitute
              waiver of that right.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Assignment:</strong> You may not assign without our written consent.
              We may assign without restriction.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Force Majeure:</strong> Neither party is liable for delays due to
              causes beyond reasonable control, including acts of God, war, terrorism,
              government actions, or internet outages.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Independent Contractors:</strong> The parties are independent contractors.
              Nothing creates an employment, agency, partnership, or joint venture.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Notices:</strong> Notices must be in writing and sent to the email
              addresses on file. Notices are effective upon delivery.
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ mt: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I have read, understood, and agree to be bound by the terms and
                conditions of this Whitelabel Partner Agreement (Version{" "}
                {AGREEMENT_VERSION})
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          onClick={handleDownloadPdf}
          startIcon={<Download />}
          color="inherit"
        >
          Download PDF
        </Button>
        <Box>
          <Button onClick={handleDecline} color="inherit" sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleAccept}
            variant="contained"
            color="primary"
            disabled={!accepted}
          >
            Accept Agreement
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default WhitelabelAgreementDialog;
